/**
 * The account page: everything this deployment holds for you, and the controls
 * to take it back.
 *
 * The order is the order of trust — who you are, which browsers are linked,
 * which assistants are connected, what they have configured, and what they
 * have been doing. Revoking a browser or a connection takes effect on the next
 * call the holder makes, so the confirm dialogs say that plainly rather than
 * asking "are you sure?" twice.
 */

import { useCallback, useState, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import { Link } from "react-router";
import type { ConfigBundle, Feature, LogicModuleInfo } from "@xtract-bot/embellisher-schema";
import { api, errorMessage } from "../api";
import {
  ConfirmModal,
  CopyField,
  EmptyState,
  ErrorAlert,
  InlineSpinner,
  LinkButton,
  Skeleton,
  SkeletonRows,
  StatusDot,
  TimeAgo,
  ViewerModal,
  formatAbsolute,
  useConfirm,
} from "../components/ui";
import { InvitationsCard } from "../components/invitations";
import { ShareFeatureModal, SharesCard, shareOutcomeMessage } from "../components/shares";
import { useAsync, usePolling } from "../useAsync";
import { useSession } from "../useSession";

/* ── Profile ──────────────────────────────────────────────────────────────── */

function Profile({ mcpUrl, bundleVersion, loading }: { mcpUrl: string | null; bundleVersion: number | null; loading: boolean }): ReactNode {
  const { account } = useSession();
  if (!account) return null;
  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h1 className="h4 mb-1 d-flex align-items-center gap-2">
              {account.username}
              {account.isAdmin ? <Badge bg="primary">Admin</Badge> : null}
            </h1>
            <p className="emb-muted mb-0 small">
              {account.email ?? "no verified email from this provider"} · signed in with {account.provider} · member
              since {formatAbsolute(account.createdAt)}
            </p>
          </div>
          {account.isAdmin ? (
            <LinkButton to="/admin" size="sm" variant="outline-primary">
              Open admin
            </LinkButton>
          ) : null}
        </div>
        <Row className="g-3">
          <Col md={8}>
            {loading || !mcpUrl ? <Skeleton height="4.4rem" /> : <CopyField label="Your MCP server URL" value={mcpUrl} />}
          </Col>
          <Col md={4}>
            <div className="emb-sunken px-3 py-2 h-100 d-flex flex-column justify-content-center">
              <span className="small emb-muted">Configuration version</span>
              <span className="fs-5 fw-semibold">
                {loading || bundleVersion === null ? <Skeleton width="3rem" height="1.4rem" /> : `v${bundleVersion}`}
              </span>
            </div>
          </Col>
        </Row>
        {account.email === null ? (
          <p className="emb-muted small mb-0 mt-3">
            Your provider did not give a verified email address. Everything works, but this account can never be an
            administrator of this deployment — administration is by verified email.
          </p>
        ) : null}
      </Card.Body>
    </Card>
  );
}

/* ── Linked browsers ──────────────────────────────────────────────────────── */

function Devices(): ReactNode {
  const devices = useAsync(() => api.devices(), []);
  const confirm = useConfirm<{ deviceId: string; name: string }>();

  const revoke = useCallback(
    () =>
      confirm.run(async (subject) => {
        await api.revokeDevice(subject.deviceId);
        await devices.reload();
      }),
    [confirm, devices],
  );

  const active = devices.data?.filter((device) => device.revokedAt === null) ?? [];
  const revoked = devices.data?.filter((device) => device.revokedAt !== null) ?? [];

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Linked browsers</span>
        {devices.reloading ? <InlineSpinner label="Refreshing" /> : null}
      </Card.Header>
      <Card.Body className={devices.loading || devices.error || active.length === 0 ? "" : "p-0"}>
        {devices.loading ? (
          <SkeletonRows rows={3} />
        ) : devices.error ? (
          <ErrorAlert error={devices.error} onRetry={() => void devices.reload()} />
        ) : active.length === 0 ? (
          <EmptyState title="No browser is linked to this account">
            Install the extension, open its side panel and choose <strong>Link this browser</strong>. Until then, every
            tool that reads a page answers “No linked browser is connected.”
          </EmptyState>
        ) : (
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th>Browser</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Extension</th>
                <th className="text-end">Linked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {active.map((device) => (
                <tr key={device.deviceId}>
                  <td>
                    <div className="fw-semibold">{device.name}</div>
                    <div className="emb-muted small text-truncate" style={{ maxWidth: "22rem" }} title={device.userAgent}>
                      {device.userAgent || "no user agent recorded"}
                    </div>
                  </td>
                  <td>
                    <StatusDot online={device.online} />
                  </td>
                  <td>
                    <TimeAgo iso={device.lastSeenAt} />
                  </td>
                  {/* The build, not just the version: an unpacked extension is
                      whatever was on disk when Chrome last loaded it, so the
                      version is the same across every rebuild and cannot say
                      whether a browser is running current code. */}
                  <td className="emb-mono" title={device.build ?? "this browser has not reported a build stamp"}>
                    {device.extensionVersion ?? "—"}
                    {device.build ? <div className="emb-muted small">{device.build.replace(/^[^+]*\+/, "")}</div> : null}
                  </td>
                  <td className="text-end">
                    <TimeAgo iso={device.createdAt} />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-danger" onClick={() => confirm.ask({ deviceId: device.deviceId, name: device.name })}>
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      {revoked.length > 0 ? (
        <Card.Footer className="emb-muted small">
          {revoked.length} revoked {revoked.length === 1 ? "link" : "links"} kept for the record; none of them can
          connect.
        </Card.Footer>
      ) : null}

      <ConfirmModal
        show={confirm.subject !== null}
        title="Revoke this browser's link?"
        confirmLabel="Revoke link"
        busy={confirm.busy}
        error={confirm.error}
        onCancel={confirm.cancel}
        onConfirm={() => void revoke()}
      >
        <p className="mb-2">
          <strong>{confirm.subject?.name}</strong> will be disconnected on its next request, and the extension there
          will need to be linked again before your assistant can see any tab in it.
        </p>
        <p className="emb-muted small mb-0">Nothing configured is lost — features and logic modules stay on this account.</p>
      </ConfirmModal>
    </Card>
  );
}

/* ── Assistant connections ────────────────────────────────────────────────── */

function Connections(): ReactNode {
  const connections = useAsync(() => api.connections(), []);
  const confirm = useConfirm<{ authId: string; clientName: string }>();

  const revoke = useCallback(
    () =>
      confirm.run(async (subject) => {
        await api.revokeConnection(subject.authId);
        await connections.reload();
      }),
    [confirm, connections],
  );

  const active = connections.data?.filter((entry) => entry.revokedAt === null) ?? [];

  return (
    <Card className="mb-4">
      <Card.Header>Assistant connections</Card.Header>
      <Card.Body className={connections.loading || connections.error || active.length === 0 ? "" : "p-0"}>
        {connections.loading ? (
          <SkeletonRows rows={2} />
        ) : connections.error ? (
          <ErrorAlert error={connections.error} onRetry={() => void connections.reload()} />
        ) : active.length === 0 ? (
          <EmptyState
            title="No assistant is connected"
            action={
              <LinkButton to="/connect" size="sm">
                Connect an assistant
              </LinkButton>
            }
          >
            Give your MCP client this account's server URL; it will send you back here to approve.
          </EmptyState>
        ) : (
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th>Client</th>
                <th>Approved</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {active.map((entry) => (
                <tr key={entry.authId}>
                  <td>
                    <div className="fw-semibold">{entry.clientName}</div>
                    <div className="emb-muted small emb-mono">{entry.clientId}</div>
                  </td>
                  <td>
                    <TimeAgo iso={entry.createdAt} />
                  </td>
                  <td>
                    <TimeAgo iso={entry.lastUsedAt} />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-danger" onClick={() => confirm.ask({ authId: entry.authId, clientName: entry.clientName })}>
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <ConfirmModal
        show={confirm.subject !== null}
        title="Revoke this connection?"
        confirmLabel="Revoke connection"
        busy={confirm.busy}
        error={confirm.error}
        onCancel={confirm.cancel}
        onConfirm={() => void revoke()}
      >
        <p className="mb-2">
          <strong>{confirm.subject?.clientName}</strong> stops working immediately — its access and refresh tokens are
          both dead on the next call, not when they expire.
        </p>
        <p className="emb-muted small mb-0">You can connect it again at any time from the Connect page.</p>
      </ConfirmModal>
    </Card>
  );
}

/* ── Configuration ────────────────────────────────────────────────────────── */

function FeatureRow({ feature, onShare }: { feature: Feature; onShare: () => void }): ReactNode {
  return (
    <li className="d-flex flex-wrap align-items-baseline gap-2 py-2 border-bottom border-light-subtle">
      <span className="fw-semibold">{feature.name}</span>
      <code className="emb-mono emb-muted">{feature.id}</code>
      {feature.enabled ? null : (
        <Badge bg="secondary" pill>
          disabled
        </Badge>
      )}
      <span className="emb-muted small ms-auto text-end">{feature.match.hosts.join(", ")}</span>
      <Button size="sm" variant="link" className="p-0" onClick={onShare} title={`Offer "${feature.name}" to someone`}>
        Share
      </Button>
    </li>
  );
}

function Configuration({ onShared }: { onShared: (message: { text: string; warning: boolean }) => void }): ReactNode {
  const config = useAsync(() => api.config(), []);
  const versions = useAsync(() => api.configVersions(), []);
  const [viewing, setViewing] = useState<{ title: string; subtitle: string; body: string | null; loading: boolean; error: string | null } | null>(null);
  const rollback = useConfirm<number>();

  const showBundle = useCallback((bundle: ConfigBundle, title: string, subtitle: string) => {
    setViewing({ title, subtitle, body: JSON.stringify(bundle, null, 2), loading: false, error: null });
  }, []);

  const showVersion = useCallback(async (version: number) => {
    setViewing({ title: `Configuration v${version}`, subtitle: "As saved", body: null, loading: true, error: null });
    try {
      const bundle = await api.configVersion(version);
      setViewing({ title: `Configuration v${version}`, subtitle: "As saved", body: JSON.stringify(bundle, null, 2), loading: false, error: null });
    } catch (cause) {
      setViewing({ title: `Configuration v${version}`, subtitle: "As saved", body: null, loading: false, error: errorMessage(cause) });
    }
  }, []);

  const doRollback = useCallback(
    () =>
      rollback.run(async (version) => {
        await api.rollback(version);
        await Promise.all([config.reload(), versions.reload()]);
      }),
    [rollback, config, versions],
  );

  const features = config.data?.features ?? [];
  const [sharing, setSharing] = useState<Feature | null>(null);

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center gap-2">
        <span>
          Configuration{config.data ? <span className="emb-muted fw-normal"> · v{config.data.bundleVersion}</span> : null}
        </span>
        {config.data ? (
          <Button size="sm" variant="outline-secondary" onClick={() => showBundle(config.data!, "Current configuration", `Version ${config.data!.bundleVersion}`)}>
            View JSON
          </Button>
        ) : null}
      </Card.Header>
      <Card.Body>
        {config.loading ? (
          <SkeletonRows rows={4} />
        ) : config.error ? (
          <ErrorAlert error={config.error} onRetry={() => void config.reload()} />
        ) : features.length === 0 ? (
          <EmptyState title="Nothing is configured yet">
            Ask your assistant for something — “on this page, show me…”, “add a right-click item that captures…” — and
            the feature it saves appears here.
          </EmptyState>
        ) : (
          <ul className="list-unstyled mb-0">
            {features.map((feature) => (
              <FeatureRow key={feature.id} feature={feature} onShare={() => setSharing(feature)} />
            ))}
          </ul>
        )}

        <ShareFeatureModal
          feature={sharing}
          send={(featureId, toEmail, note) => api.share(featureId, toEmail, note)}
          onClose={() => setSharing(null)}
          onShared={(share) => onShared(shareOutcomeMessage(share))}
        />

        <h3 className="h6 mt-4 mb-2">History</h3>
        {versions.loading ? (
          <SkeletonRows rows={3} />
        ) : versions.error ? (
          <ErrorAlert error={versions.error} onRetry={() => void versions.reload()} />
        ) : (versions.data?.length ?? 0) === 0 ? (
          <p className="emb-muted small mb-0">No version has been saved yet.</p>
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Version</th>
                <th>Saved</th>
                <th>By</th>
                <th>Note</th>
                <th className="text-end">Features</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {versions.data?.map((entry) => (
                <tr key={entry.version}>
                  <td className="emb-mono">
                    v{entry.version}
                    {entry.current ? (
                      <Badge bg="primary" className="ms-2">
                        current
                      </Badge>
                    ) : null}
                  </td>
                  <td>
                    <TimeAgo iso={entry.createdAt} />
                  </td>
                  <td className="emb-muted">{entry.createdBy}</td>
                  <td className="emb-muted">{entry.note ?? "—"}</td>
                  <td className="text-end">{entry.featureCount}</td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="link" className="p-0 me-3" onClick={() => void showVersion(entry.version)}>
                      View
                    </Button>
                    {entry.current ? null : (
                      <Button size="sm" variant="link" className="p-0" onClick={() => rollback.ask(entry.version)}>
                        Restore
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <ViewerModal
        show={viewing !== null}
        title={viewing?.title ?? ""}
        {...(viewing?.subtitle ? { subtitle: viewing.subtitle } : {})}
        body={viewing?.body ?? null}
        loading={viewing?.loading ?? false}
        error={viewing?.error ?? null}
        onHide={() => setViewing(null)}
      />

      <ConfirmModal
        show={rollback.subject !== null}
        title={`Restore version ${rollback.subject ?? ""}?`}
        confirmLabel="Restore"
        variant="primary"
        busy={rollback.busy}
        error={rollback.error}
        onCancel={rollback.cancel}
        onConfirm={() => void doRollback()}
      >
        <p className="mb-2">
          Version {rollback.subject} becomes current again, saved as a <em>new</em> version — history is append-only, so
          nothing is overwritten and you can undo this the same way.
        </p>
        <p className="emb-muted small mb-0">Every connected browser reloads its configuration immediately.</p>
      </ConfirmModal>
    </Card>
  );
}

/* ── Logic modules ────────────────────────────────────────────────────────── */

function LogicModules(): ReactNode {
  const modules = useAsync(() => api.logic(), []);
  const [viewing, setViewing] = useState<{ module: LogicModuleInfo; body: string | null; loading: boolean; error: string | null } | null>(null);

  const showSource = useCallback(async (module: LogicModuleInfo) => {
    setViewing({ module, body: null, loading: true, error: null });
    try {
      const source = await api.logicSource(module.moduleId);
      setViewing({ module, body: source, loading: false, error: null });
    } catch (cause) {
      setViewing({ module, body: null, loading: false, error: errorMessage(cause) });
    }
  }, []);

  return (
    <Card className="mb-4">
      <Card.Header>Logic modules</Card.Header>
      <Card.Body className={modules.loading || modules.error || (modules.data?.length ?? 0) === 0 ? "" : "p-0"}>
        {modules.loading ? (
          <SkeletonRows rows={2} />
        ) : modules.error ? (
          <ErrorAlert error={modules.error} onRetry={() => void modules.reload()} />
        ) : (modules.data?.length ?? 0) === 0 ? (
          <EmptyState title="No logic modules">
            Most features need none. Your assistant writes one only when a calculation or a multi-step interaction
            cannot be expressed as extractors and templates — and you can read its source here when it does.
          </EmptyState>
        ) : (
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th>Module</th>
                <th>Version</th>
                <th>Size</th>
                <th>Saved</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {modules.data?.map((module) => (
                <tr key={module.moduleId}>
                  <td>
                    <div className="emb-mono fw-semibold">{module.moduleId}</div>
                    {module.notes ? <div className="emb-muted small">{module.notes}</div> : null}
                  </td>
                  <td className="emb-mono">v{module.version}</td>
                  <td className="emb-muted">{(module.sizeBytes / 1024).toFixed(1)} kB</td>
                  <td>
                    <TimeAgo iso={module.createdAt} />
                  </td>
                  <td className="text-end">
                    {module.hasSource ? (
                      <Button size="sm" variant="outline-secondary" onClick={() => void showSource(module)}>
                        View source
                      </Button>
                    ) : (
                      <span className="emb-muted small">uploaded as wasm</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <ViewerModal
        show={viewing !== null}
        title={viewing ? `${viewing.module.moduleId} · v${viewing.module.version}` : ""}
        subtitle={viewing ? `${viewing.module.language} · sha256 ${viewing.module.sha256.slice(0, 16)}…` : ""}
        body={viewing?.body ?? null}
        loading={viewing?.loading ?? false}
        error={viewing?.error ?? null}
        onHide={() => setViewing(null)}
      />
    </Card>
  );
}

/* ── Activity ─────────────────────────────────────────────────────────────── */

export function ActivityFeed({ load, title = "Recent assistant activity" }: { load: (signal: AbortSignal) => Promise<{ events: Array<{ id: string; at: string; tool: string; summary: string; ok: boolean; durationMs: number; clientName: string; error: string | null }> }>; title?: string }): ReactNode {
  const activity = useAsync(load, []);
  usePolling(() => void activity.reload(), 10_000);

  const events = activity.data?.events ?? [];

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>{title}</span>
        <span className="emb-muted small">updates every 10s</span>
      </Card.Header>
      <Card.Body>
        {activity.loading ? (
          <SkeletonRows rows={5} />
        ) : activity.error ? (
          <ErrorAlert error={activity.error} onRetry={() => void activity.reload()} />
        ) : events.length === 0 ? (
          <EmptyState title="Nothing yet">
            Every tool call an assistant makes shows here, and live in the extension's Activity view — the tool, a
            one-line summary and whether it worked. Never the arguments.
          </EmptyState>
        ) : (
          <div>
            {events.map((event) => (
              <div className="emb-activity-row" key={event.id}>
                <span className="emb-tool-name">{event.tool}</span>
                <span className={event.ok ? "" : "text-danger"}>
                  {event.summary}
                  {event.error ? <span className="d-block emb-muted small">{event.error}</span> : null}
                </span>
                <span className="emb-muted small text-nowrap" title={`${event.clientName} · ${formatAbsolute(event.at)}`}>
                  {event.durationMs} ms · <TimeAgo iso={event.at} />
                </span>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/* ── The page ─────────────────────────────────────────────────────────────── */

export function Account(): ReactNode {
  const me = useAsync(() => api.me(), []);
  const shares = useAsync(() => api.shares(), []);

  // Accepting an offer writes a new bundle version, so the configuration card
  // has to be told; the key remounts it rather than threading a reload handle
  // through three components for the one thing that changes it from outside.
  const [configKey, setConfigKey] = useState(0);
  const [outcome, setOutcome] = useState<{ text: string; warning: boolean } | null>(null);
  const afterShareChange = useCallback(() => {
    void shares.reload();
    setConfigKey((key) => key + 1);
    void me.reload();
  }, [shares, me]);

  return (
    <Container style={{ maxWidth: "68rem" }}>
      {me.error ? <ErrorAlert error={me.error} onRetry={() => void me.reload()} /> : null}
      <Profile mcpUrl={me.data?.mcpUrl ?? null} bundleVersion={me.data?.bundleVersion ?? null} loading={me.loading} />
      <Devices />
      <Connections />
      <InvitationsCard />
      {outcome ? (
        <Alert variant={outcome.warning ? "warning" : "success"} onClose={() => setOutcome(null)} dismissible>
          {outcome.text}
        </Alert>
      ) : null}
      <SharesCard
        incoming={shares.data?.incoming ?? []}
        outgoing={shares.data?.outgoing ?? []}
        loading={shares.loading}
        onChanged={afterShareChange}
      />
      <Configuration
        key={configKey}
        onShared={(message) => {
          setOutcome(message);
          afterShareChange();
        }}
      />
      <LogicModules />
      <ActivityFeed load={(signal) => api.activity(25, signal)} />
    </Container>
  );
}
